import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { LockOutlined } from '@ant-design/icons';
import { Checkbox } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import DocumentsContext from '@totem/components/documents/report/documentsContext';
import { DocumentSummaryEntry } from '@totem/components/documents/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { isMemberOfAny } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { formatDate, isNotNull } from '@totem/utilities/common';
import { DOCUMENTS_ENDPOINT } from '@totem/utilities/endpoints';
const DocumentsLinkTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    data,
    loading,
    totalRecords,
    setDocumentSelected,
    selectedDocuments,
  } = useContext(DocumentsContext);
  const { handleTableChange, input } = useContext(InputContext);
  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Trigger download
      const anchor = document.createElement('a');
      anchor.href = url;

      anchor.download = fileName;

      document.body.appendChild(anchor);

      anchor.click();

      // Cleanup
      window.URL.revokeObjectURL(url);

      document.body.removeChild(anchor);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Download error:', error);
    }
  };
  const columns: ColumnProps<DocumentSummaryEntry>[] = [
    {
      title: '',
      dataIndex: 'document.id',
      key: 'select',
      render: (_, record: DocumentSummaryEntry) => {
        const hasPermissions =
          record.document.securityGroups.length === 0 ||
          isMemberOfAny(userProfile, record.document.securityGroups, {
            regionId: record.region.id,
            buildingId: record.building.id,
            controlSystemId: record.controlSystem.id,
          });
        if (!hasPermissions) {
          return <LockOutlined style={{ color: 'red' }} />;
        }
        return (
          <Checkbox
            checked={
              selectedDocuments.findIndex(
                (chk) => chk.document.id === record.document.id,
              ) >= 0
            }
            onChange={(evt) => setDocumentSelected(record, evt.target.checked)}
          />
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'document.displayName',
      key: 'name',
      render: (_, record: DocumentSummaryEntry) => {
        const hasPermissions =
          record.document.securityGroups.length === 0 ||
          isMemberOfAny(userProfile, record.document.securityGroups, {
            regionId: record.region.id,
            buildingId: record.building.id,
            controlSystemId: record.controlSystem.id,
          });
        if (!hasPermissions) {
          return <span>{record.document.displayName}</span>;
        }
        return (
          <button
            type="button"
            onClick={() =>
              handleDownload(
                `${DOCUMENTS_ENDPOINT}/${record.document.id}/${record.document.version}`,
                record.document.filename,
              )
            }
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'inherit',
              cursor: 'pointer',
              textDecoration: 'underline',
              font: 'inherit',
            }}
          >
            {record.document.displayName}
          </button>
        );
      },
      sorter: true,
    },
    {
      title: 'Ref Type',
      dataIndex: 'document.objectType',
      key: 'refType',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{record.document.objectType}</span>
      ),
      sorter: true,
    },
    {
      title: 'Ref Name',
      dataIndex: 'document.objectDisplay',
      key: 'refName',
      render: (_, record: DocumentSummaryEntry) => {
        switch (record.document.objectType) {
          case 'Device':
            return (
              <Link to={`/dashboard/devices/${record.document.objectId}`}>
                {record.document.objectDisplay}
              </Link>
            );
          case 'Maintenance Window':
            return (
              <Link
                to={`/dashboard/maintenanceWindows/${record.document.objectId}`}
              >
                {record.document.objectDisplay}
              </Link>
            );
          default:
            return <span>{record.document.objectDisplay}</span>;
        }
      },
      sorter: true,
    },
    {
      title: 'Region',
      dataIndex: 'region.name',
      key: 'region',
      render: (_, record: DocumentSummaryEntry) => {
        return <span>{record.region.name}</span>;
      },
      sorter: true,
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      render: (_, record: DocumentSummaryEntry) => {
        if (record.building.id !== '') {
          return (
            <Link to={`/dashboard/buildings/${record.building.id}`}>
              {record.building.name}
            </Link>
          );
        }
        return <span>{record.building.name}</span>;
      },
      sorter: true,
    },
    {
      title: 'Control System',
      dataIndex: 'controlSystem.name',
      key: 'controlSystem',
      render: (_, record: DocumentSummaryEntry) => {
        if (record.controlSystem.id !== '') {
          return (
            <Link to={`/dashboard/controlsystems/${record.controlSystem.id}`}>
              {record.controlSystem.name}
            </Link>
          );
        }
        return <span>{record.controlSystem.name}</span>;
      },
      sorter: true,
    },
    {
      title: 'Created By',
      dataIndex: 'document.createdBy',
      key: 'createdBy',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{record.document.createdBy}</span>
      ),
      sorter: true,
    },
    {
      title: 'Updated At',
      dataIndex: 'document.updatedAt',
      key: 'updatedAt',
      render: (_, record: DocumentSummaryEntry) => (
        <span>{formatDate(record.document.updatedAt)}</span>
      ),
      sorter: true,
    },
  ];
  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(data) ? data : []}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.document.id}
    />
  );
};
export default DocumentsLinkTable;
